import { FC, ReactNode } from 'react';
import type { EstadoCatalogoProdutos } from '@/hooks/useCatalogoProdutos';
import type { RecortePainel } from '@/types/dashboards';
import { comecoDeMes, descreverPeriodo, descreverProdutos, filtroAtivo, type FiltroPaineis } from '@/utils/filtroPaineis';
import { fmtData } from '@/utils/painelFormat';
import AvisoComecoDeMes from './AvisoComecoDeMes';
import SeletorPeriodo from './SeletorPeriodo';
import SeletorProdutos from './SeletorProdutos';

interface Props {
  filtro: FiltroPaineis;
  onChange: (filtro: FiltroPaineis) => void;
  onLimpar: () => void;
  /** O recorte que o servidor aplicou (ausente em snapshot anterior ao filtro). */
  recorte?: RecortePainel;
  catalogo: EstadoCatalogoProdutos;
  recalculando: boolean;
  /** Filtro recusado pelo servidor. */
  erro: string | null;
  /** O snapshot é de antes do filtro: a tela mostra o padrão até a próxima atualização. */
  indisponivel: boolean;
  /** Partes do filtro que a aba não usa (`calendario`, `produtos`): somem da barra, com o porquê. */
  naoSeAplica?: string[];
  /** Último dia com dado da aba (`frescor.dados_ate`): no começo do mês atual, a barra avisa. */
  dadosAte?: string | null;
}

/** Por que a aba não usa parte do filtro (hoje só a DRE, que é contábil). */
function textoNaoSeAplica(partes: string[]): string | null {
  const calendario = partes.includes('calendario');
  const produtos = partes.includes('produtos');
  if (calendario && produtos) {
    return 'Esta aba é contábil: vai por mês comum, pelos meses inteiros que o período toca, e não abre por produto.';
  }
  if (calendario) return 'Esta aba vai por mês comum, pelos meses inteiros que o período toca.';
  if (produtos) return 'Esta aba não abre por produto.';
  return null;
}

const Linha: FC<{ testId: string; tom?: 'alerta' | 'neutro'; children: ReactNode }> = ({ testId, tom, children }) => (
  <p
    role={tom === 'alerta' ? 'alert' : undefined}
    data-testid={testId}
    className={`text-xs ${tom === 'alerta' ? 'text-theme-danger' : 'text-content-muted'}`}
  >
    {children}
  </p>
);

const ComecoDoMes: FC<{ recorte?: RecortePainel; dadosAte?: string | null; onMesAnterior: () => void }> = ({
  recorte,
  dadosAte,
  onMesAnterior,
}) => {
  const comeco = comecoDeMes(recorte, dadosAte);
  return comeco ? <AvisoComecoDeMes comeco={comeco} onMesAnterior={onMesAnterior} /> : null;
};

/** O que está na tela, em uma linha — é o que sobra no PDF, sem os botões. */
const Resumo: FC<{ recorte: RecortePainel; recalculando: boolean }> = ({ recorte, recalculando }) => (
  <p className="text-sm text-content-secondary" data-testid="filtros-resumo" aria-live="polite">
    <span className="font-medium text-content-primary">{descreverPeriodo(recorte)}</span>
    {' · '}
    {descreverProdutos(recorte)}
    {recalculando && <span className="ml-2 text-content-muted">· atualizando…</span>}
  </p>
);

/**
 * Filtro dos painéis: período (atalhos, calendário e tipo de mês) e produtos. O filtro vive
 * na URL e vale para todas as abas que já o aceitam; o servidor monta a aba de novo e
 * devolve o recorte efetivo, que aparece no resumo. Plano: `docs/prompts/frente_paineis_filtros_plano_mae.md`.
 */
const BarraFiltros: FC<Props> = ({
  filtro,
  onChange,
  onLimpar,
  recorte,
  catalogo,
  recalculando,
  erro,
  indisponivel,
  naoSeAplica = [],
  dadosAte,
}) => (
  <section
    aria-label="Filtros do painel"
    data-testid="filtros-paineis"
    className="relative flex flex-col gap-3 rounded-xl border border-theme-border bg-surface-card p-4"
  >
    <div className="print-hide flex flex-col gap-3">
      <SeletorPeriodo
        filtro={filtro}
        recorte={recorte}
        onChange={onChange}
        semCalendario={naoSeAplica.includes('calendario')}
      />
      {!naoSeAplica.includes('produtos') && (
        <SeletorProdutos
          catalogo={catalogo.produtos}
          carregando={catalogo.carregando}
          selecionados={filtro.produtos}
          onChange={(produtos) => onChange({ ...filtro, produtos })}
        />
      )}
      {textoNaoSeAplica(naoSeAplica) && <Linha testId="filtros-nao-se-aplica">{textoNaoSeAplica(naoSeAplica)}</Linha>}
    </div>
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-theme-border pt-3">
      {recorte ? <Resumo recorte={recorte} recalculando={recalculando} /> : <span />}
      {filtroAtivo(filtro) && (
        <button
          type="button"
          onClick={onLimpar}
          data-testid="filtros-limpar"
          className="print-hide inline-flex items-center gap-1 text-xs text-content-secondary hover:text-content-primary"
        >
          <span aria-hidden className="material-symbols-outlined text-[14px] leading-none">
            filter_alt_off
          </span>
          Voltar ao padrão
        </button>
      )}
    </div>
    <ComecoDoMes recorte={recorte} dadosAte={dadosAte} onMesAnterior={() => onChange({ ...filtro, periodo: 'mes_anterior', inicio: null, fim: null })} />
    {erro && (
      <Linha testId="filtros-erro" tom="alerta">
        {erro}
      </Linha>
    )}
    {indisponivel && (
      <Linha testId="filtros-indisponivel" tom="alerta">
        Este snapshot é de antes do filtro: a tela mostra o período padrão. O filtro passa a valer depois da próxima
        atualização do painel.
      </Linha>
    )}
    {recorte?.ajustado && (
      <Linha testId="filtros-ajustado">
        Os dados guardados começam em {fmtData(recorte.janela.inicio)}: o período foi ajustado a partir dessa data.
      </Linha>
    )}
    {catalogo.erro && <Linha testId="filtros-catalogo-erro">Não foi possível carregar a lista de produtos.</Linha>}
  </section>
);

export default BarraFiltros;
